import React, { Fragment, useContext, useState, useEffect, useMemo } from 'react';
import { Card, CardBody, Table } from 'reactstrap';
import HeaderCard from '../../../../../Common/Component/HeaderCard';
import CustomizerContext from '../../../../../../_helper/Customizer';
import { BranchApi, CostCentreApi, CostCentreCategoryApi, UnitApi } from '../../../../../../api';
import { toast } from 'react-toastify';
import DataTableComponent from '../../../../../Common/Component/DataTableComponent';
import ViewModal from '../../../../../Common/Component/ViewModal';
import useAxiosPrivate from '../../../../../../Hooks/useAxiosPrivate';
import { Alias, Branch, CostCentre, CostCentreCategory, DateCreated, DateModified, Department, Description, Name, Organization, ShortName, Status, Unit } from '../../../../../../Constant';
import { base_form_fields, createDropdownField, short_name_field } from '../../../../../../Data/FormField/PublicData';
import { useParams } from 'react-router-dom';
import { formatDateWithTimeZone } from '../../../../../../_helper/dateHelper';
import OrganisationContext from '../../../../../../_helper/System/Organisation';

const CostCentreList = () => {
  const { layoutURL } = useContext(CustomizerContext);
  const axiosPrivate = useAxiosPrivate();
  const { organisation_id  } = useContext(OrganisationContext);
  const unitApi = UnitApi(organisation_id);
  const costCentreCategoryApi = CostCentreCategoryApi(organisation_id);
  const branchApi = BranchApi(organisation_id);
  const costCentreApi = CostCentreApi(organisation_id);

  const [allData, setAllData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [formFields, setFormFields] = useState(base_form_fields);
  const [isNewFieldAdded, setIsNewFieldAdded] = useState(false);

  const unitDropdownField = createDropdownField(axiosPrivate,
    'unit_id',
    'unit_id',
    `${Unit}`,
    `${unitApi}`,
    '',
    12
  );

  const branchDropdownField = createDropdownField(axiosPrivate,
    'branch_id',
    'branch_id',
    `${Branch}`,
    `${branchApi}`,
    '',
    12
  );

  const costCentreCategoryDropdownField = createDropdownField(axiosPrivate,
    'cost_centre_category_id',
    'cost_centre_category_id',
    `${CostCentreCategory}`,
    `${costCentreCategoryApi}`,
    '',
    12
  );

  useEffect(() => {

    const fetchData = async () => {
      try {
        if (!isNewFieldAdded) {
          const indexOfName = formFields.findIndex((formField) => formField.id === 'name');
          const updatedFields = [unitDropdownField, branchDropdownField, costCentreCategoryDropdownField, ...formFields];
         
          updatedFields.splice(indexOfName + 2, 0, short_name_field());
          await updatedFields[0].fetchOptions();
          await branchDropdownField.fetchOptions();
          await costCentreCategoryDropdownField.fetchOptions();
          
          setFormFields(updatedFields);
          setIsNewFieldAdded(true);
        }
      } catch (error) {
        //console.error('Error fetching options:', error);
      }
    };

    fetchData();

  }, [formFields, isNewFieldAdded, unitDropdownField, branchDropdownField, costCentreCategoryDropdownField]);


  const getAllCostCentreData = async () => {
      try {
          await axiosPrivate.get(costCentreApi).then((resp) => {
              setAllData(resp.data);
          });
      } catch (error) {
          //console.log('cancelled', error);
      } finally {
        setIsLoading(false);
      }
  };

  useEffect(() => {
    const abortController = new AbortController();
    getAllCostCentreData();

    return () => {
      abortController.abort();
    };
  }, [setAllData]);

  const handleView = (row) => {
    setViewModalOpen(true);
    setEditMode(false);
    setSelectedRowData(row);
  };

  const handleEdit = (row) => {
    // Handle the Edit action
    setViewModalOpen(true);
    setEditMode(true);
    setSelectedRowData(row);
  };

  const handleEditRec =  async (id, editRecord) => {
    const editRecordTemp = {
        unit_id: editRecord.unit_id,
        branch_id: editRecord.branch_id,
        cost_centre_category_id: editRecord.cost_centre_category_id,
        name: editRecord.name,
        short_name: editRecord.short_name,
        description: editRecord.description,
        is_active: editRecord.is_active,
    };
    
    try {
        await axiosPrivate.put(`${costCentreApi}/${id}`, editRecordTemp).then((resp) => {
          const updatedData = allData.map((row) =>
            row.id === selectedRowData.id ? { ...row, ...resp.data } : row
          );

          setAllData(updatedData);

          toast.success(`${resp.data.name} is successfully updated.`)
          handleCloseModal()
        });
    } catch (error) {
        if (error.response && error.response.status === 422) {
          toast.error('Validation Errors');
        } else if (error.response && error.response.status === 400) {
          toast.error(error.response.data.detail);
        } else {
          toast.error('Form submission error');
        }
        throw error;
    }
  };

  const memoizedHandleEdit = useMemo(() => handleEditRec, [allData, selectedRowData]);

  const handleCloseModal = () => {
    setViewModalOpen(false);
  };

  const handleDelete = async (row) => {
    // Prompt for confirmation
    const shouldDelete = window.confirm('Are you sure you want to delete this record?');

    if (!shouldDelete) {
      return; 
    }

    try {
      const response = await axiosPrivate.delete(`${costCentreApi}/${row.id}`);

      if (response.status === 204) {
        setAllData((prevData) => prevData.filter((item) => item.id !== row.id));
        toast.success('Record Delete successfully');
      } else {
        toast.error('Delete request failed:', response.statusText);
      }
    } catch (error) {
      console.error('Error deleting record:', error);
    }
  };

  const addBtn = `${process.env.PUBLIC_URL}/organisations/companies/${organisation_id}/structures/cost-centres/create/${layoutURL}`;
  const tableColumns = [
    {
      name: `${CostCentre}`,
      selector: row => `${row.name}`,
      sortable: true,
      },
      {
        name: `${Alias}`,
        selector: row => `${row.short_name}`,
        sortable: true,
        center: false,
      },
      {
        name: `${Unit}`,
        selector: row => `${row.unit.name}`,
        sortable: true,
        center: false,
      },
      {
        name: `${Branch}`,
        selector: row => `${row.branch.name}`,
        sortable: true,
        center: false,
      },
      {
        name: `${CostCentreCategory}`,
        selector: row => `${row.cost_centre_category.name}`,
        sortable: true,
        center: false,
      },
      /* {
        name: `${Description}`,
        selector: row => row.description,
        wrap: true,
        sortable: true,
        center: false,
        format: row => `${row.description.slice(0, 30)}...`,
      }, */
      {
        name: `${Status}`,
        selector: row => row.is_active,
        sortable: true,
        center: false,
        cell: row => (row.is_active ? <span className="badge badge-light-success">Active</span> : <span className="badge badge-light-danger">Inactive</span>)
      },
      {
        name: `${DateCreated}`,
        selector: row => `${formatDateWithTimeZone(row.created_at)}`,
        sortable: true,
        center: false,
      },
      {
        name: `${DateModified}`,
        selector: row => `${formatDateWithTimeZone(row.updated_at)}`,
        sortable: true,
        center: false,
      },

  ];

  return (
    <Fragment>
      <div className='email-right-aside bookmark-tabcontent contacts-tabs'>
                <div className='email-body radius-left'>
                  <div className='ps-0'>
                    <Card>
                      <HeaderCard title="List Cost Centres" btnUrl={addBtn} />
                      <CardBody>
                        <DataTableComponent 
                          tabledata={allData}
                          tableColumns={tableColumns}
                          isLoading={isLoading}
                          onView={handleView}
                          onDelete={handleDelete}
                          onEdit={handleEdit}
                        />
                      </CardBody>
                    </Card>
                  </div>
                </div>
      </div>
        
      <ViewModal 
        isOpen={viewModalOpen} 
        toggle={() => setViewModalOpen(!viewModalOpen)} 
        rowData={selectedRowData} 
        formFields={formFields}
        editMode={editMode}
        onEdit={memoizedHandleEdit}
      >
        {selectedRowData && (
          <>
            <Table>
              <tbody>
                <tr className='border-bottom-primary'>
                  <th scope='row'>{CostCentre}</th>
                  <td>{selectedRowData.name}</td>
                </tr>
                <tr className='border-bottom-secondary'>
                  <th scope='row'>{Alias}</th>
                  <td>{selectedRowData.short_name}</td>
                </tr>
                <tr className='border-bottom-primary'>
                  <th scope='row'>{Description}</th>
                  <td>{selectedRowData.description}</td>
                </tr>
                <tr className='border-bottom-primary'>
                  <th scope='row'>{Unit}</th>
                  <td>{selectedRowData.unit.name}</td>
                </tr>
                <tr className='border-bottom-primary'>
                  <th scope='row'>{Branch}</th>
                  <td>{selectedRowData.branch.name}</td>
                </tr>
                <tr className='border-bottom-primary'>
                  <th scope='row'>{CostCentreCategory}</th>
                  <td>{selectedRowData.cost_centre_category.name}</td>
                </tr>
                <tr className='border-bottom-primary'>
                  <th scope='row'>{Status}</th>
                  <td>{selectedRowData.is_active ? <span className="badge badge-light-success">Active</span> : <span className="badge badge-light-danger">Inactive</span>}</td>
                </tr>
                <tr className='border-bottom-secondary'>
                  <th scope='row'>{DateCreated}</th>
                  <td>{formatDateWithTimeZone(selectedRowData.created_at)}</td>
                </tr>
                <tr className='border-bottom-primary'>
                  <th scope='row'>{DateModified}</th>
                  <td>{formatDateWithTimeZone(selectedRowData.updated_at)}</td>
                </tr>
              </tbody>
            </Table>
          </>
        )}
        
      </ViewModal>
    </Fragment>
  );
};

export default CostCentreList;